/**
 * Estados posibles de un archivo
*/
export type FileStatus = 'loading' | 'success' | 'error' | 'success load';

export interface ProcessedFile {
    estado: FileStatus;
    name: string;
    src?: string;
    type?: string;
    size?: number;
    nameDownload?: string;
    error?: string;
}

export interface FileProcessorConfig {
    maxSizeInMB?: number;
    allowedTypes?: string[];
    onProgress?: (processed: number, total: number) => void;
}

const DEFAULT_ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
];

/**
 * @description Procesa archivos seleccionados con validacion y conversion asincrona
 * @param event - Evento del input file
 * @param callback - Callback que recibe el array actualizado de archivos
 * @param config - Configuracion opcional
 */
export function processMultimediaFiles(
    event: any,
    callback: (files: ProcessedFile[]) => void,
    config: FileProcessorConfig = {}
): void {
    const {
        maxSizeInMB = 5,
        allowedTypes = DEFAULT_ALLOWED_TYPES,
        onProgress,
    } = config;

    if (!event?.target?.files?.length) {
        callback([]);
        return;
    }

    const files = event.target.files;
    const processedFiles: ProcessedFile[] = [];
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    let processedCount = 0;

    // Funcion para verificar si terminamos de procesar todos
    const checkComplete = () => {
        if (processedCount === files.length) {
            callback(processedFiles);
        } else if (onProgress) {
            onProgress(processedCount, files.length);
        }
    };

    for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validacion de tamano
        if (file.size > maxSizeInBytes) {
            console.warn(
                `Archivo muy grande: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`
            );
            processedFiles.push({
                name: file.name,
                estado: 'error',
                error: `Archivo excede ${maxSizeInMB}MB`,
                size: file.size,
                type: file.type,
            });
            processedCount++;
            checkComplete();
            continue;
        }

        // Validacion de tipo
        if (!allowedTypes.includes(file.type)) {
            console.warn(`Tipo no soportado: ${file.name}`);
            processedFiles.push({
                name: file.name,
                estado: 'error',
                error: 'Formato no soportado',
                size: file.size,
                type: file.type,
            });
            processedCount++;
            checkComplete();
            continue;
        }

        // Convertir a base64
        const reader = new FileReader();
        reader.onload = () => {
            processedFiles.push({
                src: reader.result as string,
                name: file.name,
                nameDownload: file.name,
                estado: 'success',
                size: file.size,
                type: file.type,
            });
            processedCount++;
            checkComplete();
        };

        reader.onerror = (error) => {
            console.error('Error al leer archivo:', file.name, error);
            processedFiles.push({
                name: file.name,
                estado: 'error',
                error: 'Error al leer archivo',
                size: file.size,
                type: file.type,
            });
            processedCount++;
            checkComplete();
        };

        reader.readAsDataURL(file);
    }
}

/**
 * @description Valida si un archivo cumple con los requisitos de tamano y tipo.
 * @param file El archivo a validar.
 * @param config Configuracion opcional que incluye el tamano maximo en MB y los tipos MIME permitidos.
 * @returns Un objeto que indica si el archivo es valido y, en caso de no serlo, un mensaje de error descriptivo.
 */
export function validateFile(
    file: File,
    config: FileProcessorConfig = {}
): { valid: boolean; error?: string } {
    const { maxSizeInMB = 5, allowedTypes = DEFAULT_ALLOWED_TYPES } = config;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
        return {
            valid: false,
            error: `Archivo excede ${maxSizeInMB}MB`,
        };
    }

    if (!allowedTypes.includes(file.type)) {
        return {
            valid: false,
            error: 'Formato no soportado',
        };
    }

    return { valid: true };
}

/**
 * @description Convierte un objeto File a una cadena en formato base64 de manera asincrona, utilizando la API de FileReader para leer el contenido del archivo y resolver la promesa con el resultado en formato base64, o rechazarla en caso de error durante la lectura del archivo.
 * @param file El objeto File que se desea convertir a base64.
 * @returns Una promesa que resuelve con la representacion en base64 del archivo, o se rechaza con un error si ocurre algun problema durante la lectura del archivo.
 */
export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
}

/**
 * @description Convierte un array de bytes (Uint8Array) a un string binario, donde cada byte se representa como un caracter en el string. Esta funcion es util para manejar datos binarios que necesitan ser convertidos a un formato de texto para su procesamiento o transmision, aunque no realiza ninguna codificacion adicional como base64, simplemente mapea cada byte a su correspondiente caracter Unicode.
 * @param bytes El array de bytes (Uint8Array) que se desea convertir a string binario.
 * @returns Un string que representa los datos binarios, donde cada caracter corresponde a un byte del array original.
 */
export function bytesToBinaryString(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return binary;
}

/**
 * @description Convierte binary string a Blob para descargas
 * @param binaryString String binario (puede ser base64)
 * @param mimeType Tipo MIME del archivo
 * @return Blob generado a partir del string binario, listo para ser descargado o procesado como archivo. Si el string es base64, se extrae el tipo y los datos correctamente para crear el Blob con el tipo adecuado. Si es un string binario puro, se convierte directamente a Blob con el tipo especificado o 'application/octet-stream' por defecto.
 */
export function binaryStringToBlob(binaryString: string, mimeType?: string): Blob {
    // Si es base64, extraer tipo y datos
    if (binaryString.includes(',')) {
        const split = binaryString.split(',');
        const type = mimeType || split[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
        const binary = split[1];

        const byteCharacters = atob(binary);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type });
    }

    // Si es binary puro
    const byteNumbers = new Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        byteNumbers[i] = binaryString.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType || 'application/octet-stream' });
}

/**
 * @description Extrae el nombre de archivo desde una Key de S3
 * - Formato esperado: "rcId;fileName" o solo "fileName"
 * @param key La Key del archivo, que puede contener un ID seguido de un punto y coma y el nombre del archivo, o solo el nombre del archivo.
 * @returns El nombre del archivo extraido de la Key, o la Key completa si no se encuentra un punto y coma.
 */
export function extractFileNameFromKey(key: string): string {
    return key.split(';').pop() || key;
}

/**
 * @description Extrae el nombre sin extension de un archivo
 * @param fileName El nombre del archivo
 * @returns El nombre del archivo sin la extension
 */
export function getFileNameWithoutExtension(fileName: string): string {
    return fileName.split('.')[0] || fileName;
}
